import { Button, Segment, Message, Icon, Dropdown, Label } from "semantic-ui-react";
import { CampaignType } from "models";
import AssetInfo from "../AssetInfo/AssetInfo";
import { isAssetProcessed, formatAssetMetadata } from "services/common";
import AssetStatusInfo from "../AssetStatusInfo/AssetStatusInfo";
import { useSelector } from "react-redux";
import ApproverAssetSelector from "../ApproverAssetSelector/ApproverAssetSelector";
import React, { useState } from "react";
import SendToStartPrintModal from "components/SendToStartPrintModal/SendToStartPrintModal";

const CampaignDisplayOriginalAssets = ({
  assets,
  processedAssets,
  campaign,
  activeUser,
  isCampaignExpired,
  isCampaignActive,
  isAdmin,
  isDownloadingAssets,
  setEditingAssets,
  downloadAssets,
  selectedAssets,
  printServiceOptions,
  setSelectedPrintServiceID,
  isSubmitting,
  onAssetSelected,
  setSelectedAssets,
  selectedPrintServiceID,
  submitSendAssets,
  agencys,
  users,
  isOwner,
  currentAssets,
  stageProcessedAssets,
  isProcessingAssets,
  originalAssets,
  t,
}) => {
  const approvers = useSelector((state) => state.approvers);
  const isApproversLoading = useSelector((state) => state.approvers.isLoading);
  const [startPrintModalOpen, setStartPrintModalOpen] = useState(false);
  const handleSubmission = () => {
    if (selectedPrintServiceID === "StartPrint") {
      // Handling for StartPrint API
      setStartPrintModalOpen(true);
    } else {
      submitSendAssets();
    }
  };
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", gap: "1rem" }}>
          <h3 style={{ marginBottom: "0" }}>{t("campaign_display_assets")}</h3>
          {campaign && campaign.type === CampaignType.DIGITAL && currentAssets && (
            <Label style={{ backgroundColor: "#4700a3", color: "white" }}>
              {originalAssets.length}
              {t("campaign_display_number_of_assets")}
            </Label>
          )}
          {campaign && campaign.type === CampaignType.PRINT && assets && (
            <Label style={{ backgroundColor: "#4700a3", color: "white" }}>
              {originalAssets.length}
              {t("campaign_display_number_of_assets")}
            </Label>
          )}
          {/* Contributors can only add/edit assets for ongoing and active campaigns if they are campaign owners, admins can always */}
          {((!isCampaignExpired && isCampaignActive && isOwner) ||
            isAdmin ||
            activeUser.isContributor) && (
            <>
              <button
                style={{ fontSize: "1rem" }}
                type="button"
                className="naked-button"
                onClick={() => setEditingAssets(true)}
                data-test="campaign_display_add_and_edit_assets_button"
              >
                <Icon name="edit outline" /> {t("campaign_display_add_and_edit_assets")}
              </button>
              {!activeUser.isContributor && (
                <ApproverAssetSelector
                  campaign={campaign}
                  assets={assets}
                  approvers={approvers.items}
                  isApproversLoading={isApproversLoading}
                  t={t}
                  naked={true}
                />
              )}
            </>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
          {isAdmin && campaign && campaign.type === CampaignType.DIGITAL && (
            <>
              <Button
                style={{ fontSize: "1rem" }}
                type="button"
                className="naked-button"
                onClick={() => stageProcessedAssets(assets)}
                disabled={assets.length === 0 || campaign.requiredResolutions?.items.length === 0}
              >
                <Icon
                  name={isProcessingAssets ? "spinner" : "clone"}
                  loading={isProcessingAssets}
                />{" "}
                {t("process_all_assets")}
              </Button>
              <Button
                style={{ fontSize: "1rem" }}
                type="button"
                className="naked-button"
                onClick={() => stageProcessedAssets(selectedAssets)}
                disabled={
                  selectedAssets.length === 0 || campaign.requiredResolutions?.items.length === 0
                }
              >
                <Icon
                  name={isProcessingAssets ? "spinner" : "clone"}
                  loading={isProcessingAssets}
                />{" "}
                {t("process_selected_assets")}
              </Button>
            </>
          )}
          <Button
            style={{ fontSize: "1rem" }}
            type="button"
            className="naked-button"
            onClick={() => downloadAssets(assets)}
            disabled={isDownloadingAssets}
          >
            <Icon
              name={isDownloadingAssets ? "spinner" : "cloud download"}
              loading={isDownloadingAssets}
            />{" "}
            {t("campaign_display_download_all_assets")}
          </Button>
          <Button
            style={{ fontSize: "1rem" }}
            type="button"
            className="naked-button"
            onClick={() => downloadAssets(selectedAssets)}
            disabled={selectedAssets.length === 0 || isDownloadingAssets}
          >
            <Icon
              name={isDownloadingAssets ? "spinner" : "cloud download"}
              loading={isDownloadingAssets}
            />{" "}
            {t("campaign_display_download_selected_assets")}
          </Button>

          {campaign && campaign.type === CampaignType.PRINT && isAdmin && (
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <span>{t("campaign_display_send_selected_assets")}</span>
              <Dropdown
                placeholder={t("select_print_service_dropdown")}
                search
                selection
                clearable
                selectOnNavigation={false}
                options={printServiceOptions}
                onChange={(_e, { value }) => setSelectedPrintServiceID(value)}
              />{" "}
              <Button
                primary
                style={{ margin: "0" }}
                loading={isSubmitting}
                disabled={selectedAssets.length === 0 || !selectedPrintServiceID}
                onClick={handleSubmission}
              >
                {t("campaign_display_send_button")}
              </Button>
              <SendToStartPrintModal
                open={startPrintModalOpen}
                setModalOpen={setStartPrintModalOpen}
                selectedAssets={selectedAssets}
                setSelectedAssets={setSelectedAssets}
                sender={activeUser}
              />
            </div>
          )}
        </div>
      </div>

      {assets && assets.length ? (
        assets.map((asset, a) => (
          <Segment.Group key={asset.id}>
            <AssetStatusInfo
              assetStatusData={{
                assetStatus: asset.status.toLowerCase(),
                assetMetaData: formatAssetMetadata(asset["metadata"], campaign.duration || []),
                assetKey: asset.id,
              }}
            />
            <Segment key={asset.id + a} className="asset-input-item">
              <AssetInfo
                asset={asset}
                isAdmin={isAdmin}
                agencys={agencys}
                users={users}
                t={t}
                onAssetSelected={onAssetSelected}
                isProcessed={isAssetProcessed(asset, processedAssets)}
                campaignView={true}
              />
            </Segment>
          </Segment.Group>
        ))
      ) : (
        <Message>
          {currentAssets.length === 0
            ? t("campaign_display_no_defined_assets")
            : t("campaign_display_no_access_assets")}
        </Message>
      )}
    </>
  );
};

export default CampaignDisplayOriginalAssets;
